import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import MetaTags from "@/components/MetaTags";
import useAnalytics from "@/hooks/useAnalytics";
import { ArrowRight, Check } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const TALLY_FORM_URL = "https://tally.so/r/wkv55R";

const FAQ_ITEMS = [
  {
    question: "What is a Featured Listing?",
    answer: "A Featured Listing gives your app priority placement in search results across the entire platform. Your app will be highlighted with a special badge, making it more visible to potential users. This is perfect for apps looking to increase their overall visibility and attract more users across all categories and sports."
  },
  {
    question: "How does Category Sponsorship work?",
    answer: "Category Sponsorship gives you exclusive top placement in your chosen category (e.g., Training & Workouts, Activity Tracking, etc.). Your app will be the first one users see when browsing that category, and you'll receive a special sponsor badge. This option includes Featured Listing benefits and is ideal for apps wanting to dominate their specific niche."
  },
  {
    question: "What are the benefits of Sport Sponsorship?",
    answer: "Sport Sponsorship provides exclusive top placement in your chosen sport (e.g., Running, Swimming, etc.). Your app will be prominently displayed to users interested in that specific sport, with a premium sponsor badge. This includes Featured Listing benefits and is perfect for sport-specific apps wanting maximum visibility in their target market."
  },
  {
    question: "Can I sponsor multiple categories or sports?",
    answer: "Yes, you can sponsor multiple categories or sports. Each sponsorship is treated separately, allowing you to maximize your visibility across different segments. Contact us for custom packages and potential discounts for multiple sponsorships."
  },
  {
    question: "How long is the commitment period?",
    answer: "All promotional plans are billed monthly, and you can cancel at any time. We recommend maintaining your sponsorship for at least 3 months to see optimal results and establish a strong presence in your chosen category or sport."
  },
  {
    question: "What happens after I click 'Upgrade Now'?",
    answer: "After clicking 'Upgrade Now', you'll be directed to a form where you can specify your preferences and contact details. Our team will review your submission and contact you within 24 hours to finalize the details and set up your promotional placement."
  }
];

const PricingPage = () => {
  const { trackEvent } = useAnalytics();

  const handleUpgradeClick = (tier: string) => {
    trackEvent('pricing_upgrade_click', { tier });
    window.open(TALLY_FORM_URL, '_blank');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags
        title="Pricing - FitnessAppSearch"
        description="Choose the perfect plan to boost your app's visibility on FitnessAppSearch. Featured listings, category sponsorship, and sport sponsorship options available."
        canonicalUrl={`${window.location.origin}/pricing`}
        imageUrl={`${window.location.origin}/og-image.png`}
      />
      
      <Navigation />
      
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Promote Your Listing</h1>
            <p className="text-xl text-muted-foreground">
              Choose the perfect plan to boost your app's visibility
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Featured Listing */}
            <Card className="relative">
              <CardHeader>
                <CardTitle>Featured</CardTitle>
                <CardDescription>Stand out from the crowd</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-6">
                  $30<span className="text-lg font-normal text-muted-foreground">/month</span>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Priority placement in search results</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Featured badge on listing</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Enhanced visibility across site</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full" 
                  onClick={() => handleUpgradeClick('featured')}
                >
                  Upgrade Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>

            {/* Category Sponsor */}
            <Card className="relative border-primary">
              {/* <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary text-primary-foreground text-sm font-medium px-3 py-1 rounded-full">
                  Popular Choice
                </span>
              </div> */}
              <CardHeader>
                <CardTitle>Category Sponsor</CardTitle>
                <CardDescription>Own your category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-6">
                  $75<span className="text-lg font-normal text-muted-foreground">/month</span>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Top placement in your category</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Sponsor badge on listing</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Category exclusivity</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Featured status included</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full" 
                  variant="default"
                  onClick={() => handleUpgradeClick('category_sponsor')}
                >
                  Upgrade Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>

            {/* Sport Sponsor */}
            <Card className="relative">
              <CardHeader>
                <CardTitle>Sport Sponsor</CardTitle>
                <CardDescription>Dominate your sport</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-6">
                  $99<span className="text-lg font-normal text-muted-foreground">/month</span>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Top placement in your sport</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Sport exclusivity</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Premium sponsor badge</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Featured status included</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Priority support</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full"
                  onClick={() => handleUpgradeClick('sport_sponsor')}
                >
                  Upgrade Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          </div>

          <div className="text-center mt-12 text-muted-foreground">
            <p>All plans include enhanced analytics and priority support.</p>
            <p className="mt-2">Have questions? Contact us for custom solutions.</p>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto mt-20">
            <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQ_ITEMS.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent>
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PricingPage; 